import type { RootParamList } from "../navigation/MainSwitchNavigator";
import type { MainStackParamList } from "../navigation/MainStackNavigator";
import { createNavigationContainerRef } from '@react-navigation/native';

export const navigationRef = createNavigationContainerRef<RootParamList>();

// Helper to check if a route is a MainStack route
const MAIN_STACK_ROUTES: (keyof MainStackParamList)[] = [
  'Main', 'Cart', 'Vendor', 'Vendors', 'VendorSettings', 'VendorRegistration',
  'ProductDetails', 'AddProduct', 'Chat', 'ArchivedChats', 'Likes', 'About',
  'Tutorial', 'Support', 'Notifications', 'DeliveryAddress', 'Orders',
  'PaymentMethods', 'Checkout', 'OrderTracking', 'ProfileSetup', 'Home',
  'PrivacyPolicy', 'TermsAndConditions', 'CartsByStore', 'VendorOrderDetails',
  'BuyerOrderDetails', 'Receipt', 'PaymentProcessingInfo', 'DeleteAccount', 'PasswordReset'
];

function isMainStackRoute(name: string): name is keyof MainStackParamList {
  return MAIN_STACK_ROUTES.includes(name as keyof MainStackParamList);
}

// Overload for root-level navigation
export function navigate<Name extends keyof RootParamList>(
  name: Name,
  params?: RootParamList[Name]
): boolean;

// Overload for MainStack navigation (nested routes)
export function navigate<Name extends keyof MainStackParamList>(
  name: Name,
  params?: MainStackParamList[Name]
): boolean;

// Implementation
export function navigate<Name extends keyof RootParamList | keyof MainStackParamList>(
  name: Name,
  params?: any
): boolean {
  if (!navigationRef.isReady()) {
    console.warn(`🧭 [NAVIGATION] Navigation not ready for route: ${name}`);
    return false;
  }

  try {
    if (isMainStackRoute(name as string)) {
      // Navigate to nested route within MainApp
      (navigationRef.navigate as any)('MainApp', {
        screen: name,
        params: params
      });
      console.log(`🧭 [NAVIGATION] Successfully navigated to nested route: ${name}`, params);
      return true;
    } else {
      // Navigate to root-level route
      (navigationRef.navigate as any)(...(params ? [name, params] : [name]));
      console.log(`🧭 [NAVIGATION] Successfully navigated to: ${name}`, params);
      return true;
    }
  } catch (error) {
    console.error(`🧭 [NAVIGATION] Failed to navigate to ${name}:`, error);
    
    // Try to handle common navigation issues
    try {
      if (isMainStackRoute(name as string)) {
        navigationRef.reset({
          index: 0,
          routes: [{ name: 'MainApp' as any, params: { screen: name, params } }],
        });
      } else {
        navigationRef.reset({
          index: 0,
          routes: [{ name: name as any }],
        });
      }
      console.log(`🧭 [NAVIGATION] Fallback reset navigation to: ${name}`);
      return true;
    } catch (fallbackError) {
      console.error(`🧭 [NAVIGATION] Fallback navigation also failed:`, fallbackError);
      return false;
    }
  }
}

export function goBack() {
  if (navigationRef.isReady()) {
    navigationRef.goBack();
  }
}

/**
 * Navigate to the Auth screen (useful for unauthenticated users in public views)
 * This resets the navigation to AuthFlow stack
 * @param returnToVendorId - Optional vendor ID to return to after sign in
 */
export function navigateToAuth(returnToVendorId?: string) {
  if (!navigationRef.isReady()) {
    console.warn('🧭 [NAVIGATION] Navigation not ready for Auth');
    return false;
  }

  try {
    // Clear public vendor view flag if on web
    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('navigateToAuth', 'true');
      
      // Store return vendor ID if provided
      if (returnToVendorId) {
        sessionStorage.setItem('returnToVendorId', returnToVendorId);
        console.log('🧭 [NAVIGATION] Stored return vendor ID:', returnToVendorId);
      }
    }
    
    // Reset to AuthFlow
    navigationRef.reset({
      index: 0,
      routes: [{ name: 'AuthFlow' as any }],
    });
    console.log('🧭 [NAVIGATION] Navigated to AuthFlow');
    return true;
  } catch (error) {
    console.error('🧭 [NAVIGATION] Failed to navigate to Auth:', error);
    return false;
  }
}

/**
 * Check if there's a vendor to return to after auth and navigate there
 * Call this after successful authentication
 */
export function checkAndNavigateToReturnVendor(): boolean {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false;
  }

  const returnToVendorId = sessionStorage.getItem('returnToVendorId');
  if (returnToVendorId) {
    sessionStorage.removeItem('returnToVendorId');
    console.log('🧭 [NAVIGATION] Returning to vendor:', returnToVendorId);
    
    // Navigate to the vendor screen
    if (navigationRef.isReady()) {
      setTimeout(() => {
        navigate('Vendor', { id: returnToVendorId } as any);
      }, 100);
      return true;
    }
  }
  return false;
}

export default {
  navigate,
  goBack,
  navigateToAuth,
  checkAndNavigateToReturnVendor,
  navigationRef,
};